// Enhanced AI Service using Netlify AI Gateway
// Integrates with existing LeadQualifierAgent and 6sense enrichment

import { setupAIGateway, fetchAIProviders, parseAIGatewayContext, type AIGatewayConfig } from '@netlify/ai/bootstrap';
import { LeadScore } from '../aiService';
import { Lead } from '../../stores/leadsStore';

export interface NetlifyAIConfig {
  siteId?: string;
  siteUrl?: string;
  enabledProviders: string[];
  fallbackProvider: string;
  costOptimization: boolean;
}

export interface EnhancedLeadScore extends LeadScore {
  aiProviderUsed: string;
  enrichmentAnalysis: {
    companyFit: number;
    intentSignals: number;
    industryMatch: number;
    urgencyLevel: number;
    riskFactors: string[];
  };
  confidenceScore: number;
  processingTime: number;
}

export interface AIAnalysisResult {
  score: number;
  reasoning: string;
  confidence: number;
  dataPoints: Record<string, any>;
  provider: string;
}

class NetlifyAIService {
  private config: NetlifyAIConfig;
  private isInitialized = false;
  private availableProviders: string[] = [];

  constructor(config?: Partial<NetlifyAIConfig>) {
    this.config = {
      siteId: import.meta.env.VITE_NETLIFY_SITE_ID,
      siteUrl: import.meta.env.VITE_NETLIFY_SITE_URL || window.location.origin,
      enabledProviders: ['claude-3-5-sonnet', 'claude-haiku', 'openai-gpt-4', 'gemini-pro'],
      fallbackProvider: 'claude-haiku',
      costOptimization: true,
      ...config
    };
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      console.log('🤖 Initializing Netlify AI Gateway...');

      // Setup AI Gateway
      await setupAIGateway({
        api: this.getNetlifyAPI(),
        env: this.getEnvironmentVars(),
        siteID: this.config.siteId,
        siteURL: this.config.siteUrl
      });

      // Fetch available providers
      this.availableProviders = await fetchAIProviders({
        api: this.getNetlifyAPI()
      }).then(providers => providers.map(p => p.key));

      console.log('✅ Netlify AI Gateway initialized with providers:', this.availableProviders);
      this.isInitialized = true;
    } catch (error) {
      console.error('❌ Failed to initialize Netlify AI Gateway:', error);
      throw new Error('Netlify AI initialization failed');
    }
  }

  async scoreLeadsEnhanced(leads: Lead[]): Promise<EnhancedLeadScore[]> {
    await this.initialize();

    console.log(`🧠 Scoring ${leads.length} leads with enhanced AI analysis...`);
    const startTime = Date.now();

    // Process leads in parallel for better performance
    const results = await Promise.all(
      leads.map(lead => this.scoreSingleLeadEnhanced(lead))
    );

    const totalTime = Date.now() - startTime;
    console.log(`✨ Enhanced scoring completed in ${totalTime}ms (${(totalTime / leads.length).toFixed(0)}ms per lead)`);

    return results;
  }

  private async scoreSingleLeadEnhanced(lead: Lead): Promise<EnhancedLeadScore> {
    const startTime = Date.now();

    try {
      // Run multiple AI analyses in parallel
      const [
        companyAnalysis,
        intentAnalysis,
        industryAnalysis,
        urgencyAnalysis
      ] = await Promise.all([
        this.analyzeCompanyData(lead),
        this.detectBuyingIntent(lead),
        this.assessIndustryFit(lead),
        this.detectUrgency(lead)
      ]);

      // Combine analyses for final score
      const enrichmentAnalysis = {
        companyFit: companyAnalysis.score,
        intentSignals: intentAnalysis.score,
        industryMatch: industryAnalysis.score,
        urgencyLevel: urgencyAnalysis.score,
        riskFactors: this.extractRiskFactors([companyAnalysis, intentAnalysis, industryAnalysis, urgencyAnalysis])
      };

      // Calculate weighted final score
      const finalScore = this.calculateWeightedScore(enrichmentAnalysis);
      const confidence = this.calculateConfidence([companyAnalysis, intentAnalysis, industryAnalysis, urgencyAnalysis]);

      const processingTime = Date.now() - startTime;

      return {
        leadId: lead.id,
        score: finalScore,
        reasoning: this.generateEnhancedReasoning(lead, enrichmentAnalysis, [companyAnalysis, intentAnalysis, industryAnalysis, urgencyAnalysis]),
        recommendations: this.generateSmartRecommendations(lead, enrichmentAnalysis),
        riskFactors: enrichmentAnalysis.riskFactors,
        nextActions: this.generateContextualActions(lead, enrichmentAnalysis),
        aiProviderUsed: companyAnalysis.provider, // Track which provider was used
        enrichmentAnalysis,
        confidenceScore: confidence,
        processingTime
      };
    } catch (error) {
      console.error(`❌ Error scoring lead ${lead.id}:`, error);

      // Fallback to basic scoring if AI fails
      return this.fallbackToBasicScoring(lead, Date.now() - startTime);
    }
  }

  private async analyzeCompanyData(lead: Lead): Promise<AIAnalysisResult> {
    const provider = this.selectOptimalProvider('company-analysis');

    const prompt = `
      Analyze this company's potential as a customer for a SaaS GTM platform:

      Company: ${lead.company}
      Industry: ${lead.enrichment?.industry || 'Unknown'}
      Size: ${lead.enrichment?.employeeCount || 'Unknown'} employees
      Revenue: ${lead.enrichment?.revenue ? `$${lead.enrichment.revenue}M` : 'Unknown'}
      Tech Stack: ${lead.enrichment?.technographics?.join(', ') || 'Unknown'}
      Department: ${lead.enrichment?.department || 'Unknown'}
      Seniority: ${lead.enrichment?.seniority || 'Unknown'}

      Score the company fit from 1-100 and provide reasoning focusing on:
      1. Company size and growth potential
      2. Technology sophistication
      3. Industry alignment with our solution
      4. Budget indicators

      Return JSON: { "score": number, "reasoning": string, "keyFactors": string[] }
    `;

    return await this.callAI(provider, 'company-analysis', prompt, {
      company: lead.company,
      enrichment: lead.enrichment
    });
  }

  private async detectBuyingIntent(lead: Lead): Promise<AIAnalysisResult> {
    const provider = this.selectOptimalProvider('intent-detection');

    const prompt = `
      Analyze buying intent signals for this lead:

      Lead: ${lead.name} at ${lead.company}
      Email: ${lead.email}
      Source: ${lead.source}
      Current Status: ${lead.status}
      Value: $${lead.value}
      Created: ${lead.createdAt}

      Available enrichment data:
      ${JSON.stringify(lead.enrichment || {}, null, 2)}

      Score buying intent from 1-100 based on:
      1. Lead source quality and engagement
      2. Enrichment signals (if available)
      3. Deal value and timeline indicators
      4. Decision maker profile

      Return JSON: { "score": number, "reasoning": string, "intentSignals": string[] }
    `;

    return await this.callAI(provider, 'intent-detection', prompt, {
      lead,
      enrichment: lead.enrichment
    });
  }

  private async assessIndustryFit(lead: Lead): Promise<AIAnalysisResult> {
    const provider = this.selectOptimalProvider('industry-fit');

    const idealIndustries = ['Technology', 'Software', 'SaaS', 'Financial Services', 'Healthcare Technology'];

    const prompt = `
      Assess industry fit for a GTM/Sales platform solution:

      Lead Industry: ${lead.enrichment?.industry || 'Unknown'}
      Company: ${lead.company}
      Department: ${lead.enrichment?.department || 'Unknown'}

      Our ideal industries: ${idealIndustries.join(', ')}

      Score industry fit from 1-100 considering:
      1. Industry alignment with our core market
      2. Typical GTM complexity in this industry
      3. Technology adoption patterns
      4. Budget allocation for sales tools

      Return JSON: { "score": number, "reasoning": string, "fitFactors": string[] }
    `;

    return await this.callAI(provider, 'industry-fit', prompt, {
      industry: lead.enrichment?.industry,
      company: lead.company
    });
  }

  private async detectUrgency(lead: Lead): Promise<AIAnalysisResult> {
    const provider = this.selectOptimalProvider('urgency-detection');

    const daysSinceCreated = Math.floor((Date.now() - new Date(lead.createdAt).getTime()) / (1000 * 60 * 60 * 24));

    const prompt = `
      Detect urgency level for this sales opportunity:

      Lead: ${lead.name} at ${lead.company}
      Status: ${lead.status}
      Value: $${lead.value}
      Days in pipeline: ${daysSinceCreated}
      Source: ${lead.source}

      Score urgency from 1-100 based on:
      1. Time sensitivity indicators
      2. Lead status and progression
      3. Deal size vs typical sales cycle
      4. Source urgency patterns

      Return JSON: { "score": number, "reasoning": string, "urgencyFactors": string[] }
    `;

    return await this.callAI(provider, 'urgency-detection', prompt, {
      lead,
      daysSinceCreated
    });
  }

  private selectOptimalProvider(taskType: string): string {
    // Cost and performance optimized provider selection
    const providerMap: Record<string, string> = {
      'company-analysis': 'openai-gpt-4',    // Best for data analysis
      'intent-detection': 'claude-3-5-sonnet', // Best for reasoning
      'industry-fit': 'gemini-pro',          // Good for categorization
      'urgency-detection': 'claude-haiku'    // Fast for simple tasks
    };

    const preferred = providerMap[taskType] || this.config.fallbackProvider;

    // Check if preferred provider is available
    if (this.availableProviders.includes(preferred)) {
      return preferred;
    }

    // Fallback to first available or default
    return this.availableProviders[0] || this.config.fallbackProvider;
  }

  private async callAI(provider: string, task: string, prompt: string, context: any): Promise<AIAnalysisResult> {
    try {
      // In a real implementation, this would call through Netlify AI Gateway
      // For now, we'll simulate the enhanced analysis
      const response = await this.simulateEnhancedAI(provider, task, prompt, context);

      return {
        score: response.score,
        reasoning: response.reasoning,
        confidence: response.confidence || 0.85,
        dataPoints: response.dataPoints || {},
        provider
      };
    } catch (error) {
      console.error(`❌ AI call failed for ${task} with ${provider}:`, error);
      throw error;
    }
  }

  private async simulateEnhancedAI(provider: string, task: string, prompt: string, context: any): Promise<any> {
    // Simulate AI processing delay based on provider
    const delays: Record<string, number> = {
      'claude-haiku': 200,
      'claude-3-5-sonnet': 800,
      'openai-gpt-4': 1200,
      'gemini-pro': 600
    };

    await new Promise(resolve => setTimeout(resolve, delays[provider] || 500));

    // Enhanced simulation based on actual enrichment data
    switch (task) {
      case 'company-analysis':
        return this.simulateCompanyAnalysis(context);
      case 'intent-detection':
        return this.simulateIntentDetection(context);
      case 'industry-fit':
        return this.simulateIndustryFit(context);
      case 'urgency-detection':
        return this.simulateUrgencyDetection(context);
      default:
        return { score: 50, reasoning: 'Basic analysis', confidence: 0.5 };
    }
  }

  private simulateCompanyAnalysis(context: any): any {
    const { company, enrichment } = context;
    let score = 50;
    const factors = [];

    // Analyze enrichment data
    if (enrichment?.employeeCount) {
      if (enrichment.employeeCount > 500) {
        score += 25;
        factors.push('Large enterprise size (500+ employees)');
      } else if (enrichment.employeeCount > 100) {
        score += 15;
        factors.push('Mid-market size (100+ employees)');
      } else {
        score += 5;
        factors.push('Small business size');
      }
    }

    if (enrichment?.revenue) {
      if (enrichment.revenue > 100) {
        score += 20;
        factors.push('High revenue ($100M+)');
      } else if (enrichment.revenue > 10) {
        score += 10;
        factors.push('Substantial revenue ($10M+)');
      }
    }

    if (enrichment?.technographics?.length) {
      score += 15;
      factors.push(`Advanced tech stack (${enrichment.technographics.length} technologies)`);
    }

    return {
      score: Math.min(100, score),
      reasoning: `Company analysis based on: ${factors.join(', ') || 'Limited data available'}`,
      confidence: enrichment ? 0.9 : 0.6,
      dataPoints: { employeeCount: enrichment?.employeeCount, revenue: enrichment?.revenue }
    };
  }

  private simulateIntentDetection(context: any): any {
    const { lead } = context;
    let score = 40;
    const signals = [];

    // Analyze lead characteristics for intent
    if (lead.source === 'Website') {
      score += 20;
      signals.push('Direct website inquiry (high intent)');
    } else if (lead.source === 'Referral') {
      score += 30;
      signals.push('Referral source (very high intent)');
    } else if (lead.source === 'Cold Email') {
      score += 5;
      signals.push('Cold outreach (low intent)');
    }

    if (lead.value > 50000) {
      score += 20;
      signals.push('High deal value indicates serious consideration');
    }

    if (lead.status === 'Qualified') {
      score += 15;
      signals.push('Already sales-qualified');
    }

    return {
      score: Math.min(100, score),
      reasoning: `Intent signals: ${signals.join(', ') || 'Standard profile'}`,
      confidence: 0.8,
      dataPoints: { source: lead.source, value: lead.value, status: lead.status }
    };
  }

  private simulateIndustryFit(context: any): any {
    const { industry, company } = context;
    let score = 50;
    const factors = [];

    const highFitIndustries = ['technology', 'software', 'saas', 'fintech', 'healthtech'];
    const mediumFitIndustries = ['finance', 'healthcare', 'consulting', 'manufacturing'];

    const industryLower = (industry || '').toLowerCase();

    if (highFitIndustries.some(ind => industryLower.includes(ind))) {
      score += 30;
      factors.push('High-fit industry for GTM solutions');
    } else if (mediumFitIndustries.some(ind => industryLower.includes(ind))) {
      score += 15;
      factors.push('Medium-fit industry with GTM needs');
    } else {
      score += 5;
      factors.push('Standard industry fit');
    }

    if (company?.toLowerCase().includes('tech') || company?.toLowerCase().includes('software')) {
      score += 10;
      factors.push('Technology-focused company');
    }

    return {
      score: Math.min(100, score),
      reasoning: `Industry fit: ${factors.join(', ') || 'Basic industry alignment'}`,
      confidence: industry ? 0.85 : 0.5,
      dataPoints: { industry, company }
    };
  }

  private simulateUrgencyDetection(context: any): any {
    const { lead, daysSinceCreated } = context;
    let score = 50;
    const factors = [];

    // Time-based urgency
    if (daysSinceCreated <= 1) {
      score += 20;
      factors.push('Very recent lead (high urgency)');
    } else if (daysSinceCreated <= 7) {
      score += 10;
      factors.push('Recent lead (medium urgency)');
    } else if (daysSinceCreated > 30) {
      score -= 10;
      factors.push('Older lead (lower urgency)');
    }

    // Value-based urgency
    if (lead.value > 100000) {
      score += 15;
      factors.push('High-value deal requires immediate attention');
    }

    // Status-based urgency
    if (lead.status === 'New') {
      score += 10;
      factors.push('New lead needs immediate follow-up');
    }

    return {
      score: Math.max(10, Math.min(100, score)),
      reasoning: `Urgency assessment: ${factors.join(', ') || 'Standard urgency level'}`,
      confidence: 0.75,
      dataPoints: { daysSinceCreated, value: lead.value, status: lead.status }
    };
  }

  private calculateWeightedScore(analysis: any): number {
    // Weighted scoring based on importance
    const weights = {
      companyFit: 0.3,
      intentSignals: 0.35,
      industryMatch: 0.2,
      urgencyLevel: 0.15
    };

    return Math.round(
      analysis.companyFit * weights.companyFit +
      analysis.intentSignals * weights.intentSignals +
      analysis.industryMatch * weights.industryMatch +
      analysis.urgencyLevel * weights.urgencyLevel
    );
  }

  private calculateConfidence(analyses: AIAnalysisResult[]): number {
    const avgConfidence = analyses.reduce((sum, a) => sum + a.confidence, 0) / analyses.length;
    return Math.round(avgConfidence * 100) / 100;
  }

  private extractRiskFactors(analyses: AIAnalysisResult[]): string[] {
    const risks = [];

    const avgScore = analyses.reduce((sum, a) => sum + a.score, 0) / analyses.length;
    if (avgScore < 40) risks.push('Low overall qualification score');

    const lowConfidence = analyses.some(a => a.confidence < 0.7);
    if (lowConfidence) risks.push('Limited data for accurate assessment');

    return risks;
  }

  private generateEnhancedReasoning(lead: Lead, analysis: any, aiResults: AIAnalysisResult[]): string {
    const insights = aiResults.map(r => r.reasoning).join('. ');
    return `Enhanced AI analysis: ${insights}. Overall assessment shows ${analysis.companyFit}/100 company fit, ${analysis.intentSignals}/100 intent signals, ${analysis.industryMatch}/100 industry match, and ${analysis.urgencyLevel}/100 urgency level.`;
  }

  private generateSmartRecommendations(lead: Lead, analysis: any): string[] {
    const recommendations = [];

    if (analysis.companyFit > 80) {
      recommendations.push('High-fit company - prioritize for senior sales rep');
    }

    if (analysis.intentSignals > 70) {
      recommendations.push('Strong buying signals - schedule demo within 24 hours');
    }

    if (analysis.urgencyLevel > 75) {
      recommendations.push('High urgency - immediate outreach required');
    }

    if (analysis.industryMatch > 85) {
      recommendations.push('Perfect industry fit - use industry-specific messaging');
    }

    return recommendations.length ? recommendations : ['Standard follow-up process'];
  }

  private generateContextualActions(lead: Lead, analysis: any): string[] {
    const actions = [];

    if (analysis.companyFit > 70 && analysis.intentSignals > 60) {
      actions.push('Schedule qualified discovery call');
    } else if (analysis.intentSignals < 40) {
      actions.push('Nurture with educational content');
    } else {
      actions.push('Standard qualification process');
    }

    if (analysis.riskFactors.length > 0) {
      actions.push('Address risk factors in outreach');
    }

    return actions;
  }

  private fallbackToBasicScoring(lead: Lead, processingTime: number): EnhancedLeadScore {
    // Fallback to basic scoring if AI fails
    let score = 50;
    if (lead.value > 50000) score += 20;
    if (lead.source === 'Referral') score += 15;
    if (lead.company?.toLowerCase().includes('tech')) score += 10;

    return {
      leadId: lead.id,
      score: Math.round(score),
      reasoning: 'Fallback scoring due to AI service unavailability',
      recommendations: ['Standard follow-up process'],
      riskFactors: ['AI analysis unavailable'],
      nextActions: ['Manual review required'],
      aiProviderUsed: 'fallback',
      enrichmentAnalysis: {
        companyFit: score,
        intentSignals: score,
        industryMatch: score,
        urgencyLevel: score,
        riskFactors: ['AI analysis failed']
      },
      confidenceScore: 0.5,
      processingTime
    };
  }

  private getNetlifyAPI(): any {
    // This would return your actual Netlify API instance
    // For now, return a mock
    return {
      get: async () => ({}),
      post: async () => ({})
    };
  }

  private getEnvironmentVars(): Record<string, { sources: string[]; value: string }> {
    return {
      NODE_ENV: { sources: ['environment'], value: 'development' }
    };
  }
}

export default NetlifyAIService;